import React, { Fragment, useRef } from 'react';
import { RadioGroup } from '@headlessui/react';
import get from 'lodash.get';

import {
    useSelectedVariantContext,
    useTreatmentSelectorUpdaterContext,
    useVariantsContext,
} from '../../context/TreatmentSelectorContext';

import Icon from '../../../../components/_ui/_blocks/Icon/Icon';
import Typography from '../../../../components/_ui/_blocks/Typography/Typography';
import Reel from '../../../../components/_ui/Reel/Reel';

import styles from './VariantSelector.module.css';

const getLabel = (selectedVariant: ReturnType<typeof useSelectedVariantContext>) => {
    const customLabel = get(selectedVariant, 'public.treatmentSelector.variantLabel');

    if (customLabel) {
        return customLabel;
    }

    return selectedVariant.type === 'ODONLY' ? 'Treatment preference' : 'Select treatment';
}

/**
 * Used to select the desired variant from a treatment.
 */
const VariantSelector = () => {
    const radioGroup = useRef<null | HTMLElement>(null);

    const variants = useVariantsContext();
    const selectedVariant = useSelectedVariantContext();
    const { updateVariant } = useTreatmentSelectorUpdaterContext();

    const handleChange = (value: number) => {
        updateVariant(variants.find((variant) => variant.id === value)!);
    };

    // If there is only one variant to select, we don't need to show the selector.
    if (variants.length === 1) {
        return null;
    }

    return (
        <RadioGroup
            ref={radioGroup}
            value={selectedVariant.id}
            onChange={handleChange}
            className="space-y-050"
        >
            <RadioGroup.Label className="block type-emphasis font-600 type-090">
                {getLabel(selectedVariant)}
            </RadioGroup.Label>
            <Reel
                numberToDisplayOnMobile={2}
                gapSize={12}
                className={styles.list}
            >
                {variants.map((variant) => {
                    const isInStock = variant.stock > 0;

                    return (
                        <RadioGroup.Option
                            key={variant.id}
                            value={variant.id}
                            className={styles.option}
                            onMouseDown={(e) => {
                                // Need this. If you click on a listbox headlessUI component, then try to
                                // click on a radio group option, the listbox list closes but the radio group
                                // isn't focused on or selected. To get around this, we listen for the mouse
                                // down event. If someone clicks this option and the focus is outside of the
                                // option list, we run the handleChange event.
                                if (!radioGroup.current || radioGroup.current.contains(document.activeElement)) {
                                    return;
                                }

                                e.preventDefault();
                                e.stopPropagation();
                                handleChange(variant.id);
                            }}
                        >
                            {({ checked }) => (
                                <Fragment>
                                    <Typography
                                        as="div"
                                        weight={checked ? '700' : undefined}
                                        className={`flex items-start gap-025 ${!isInStock ? 'opacity-50' : ''}`}
                                        data-initial-reel-item={checked ? 'true' : undefined}
                                        size="090"
                                    >
                                        {variant.name}
                                        <Icon
                                            icon="success-light"
                                            size="xsmall"
                                            alt="Selected"
                                            className={`flex-grow-0 mt-050 ${!checked ? 'opacity-0' : ''}`}
                                        />
                                    </Typography>
                                    <Typography
                                        as="div"
                                        size="070"
                                        color={isInStock ? 'quiet' : 'error-dark'}
                                        className="mt-050"
                                    >
                                        {isInStock ? 'In stock' : 'Out of stock'}
                                    </Typography>
                                </Fragment>
                            )}
                        </RadioGroup.Option>
                    );
                })}
            </Reel>
        </RadioGroup>
    );
};

export default VariantSelector;
